/**
 * Custom hooks for accessing theme values in a type-safe way
 */

import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import type { Breakpoint } from '@mui/material/styles';

/**
 * Hook to access the MUI theme with custom properties
 */
export const useTheme = () => {
  return useMuiTheme();
};

/**
 * Hook to get responsive breakpoint helpers
 * @example
 * const { isMobile, isTablet, isDesktop } = useBreakpoints();
 */
export const useBreakpoints = () => {
  const theme = useMuiTheme();

  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.only('lg'));
  const isXl = useMediaQuery(theme.breakpoints.only('xl'));

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const upSm = useMediaQuery(theme.breakpoints.up('sm'));
  const upMd = useMediaQuery(theme.breakpoints.up('md'));
  const upLg = useMediaQuery(theme.breakpoints.up('lg'));
  const upXl = useMediaQuery(theme.breakpoints.up('xl'));

  const downSm = useMediaQuery(theme.breakpoints.down('sm'));
  const downMd = useMediaQuery(theme.breakpoints.down('md'));
  const downLg = useMediaQuery(theme.breakpoints.down('lg'));
  const downXl = useMediaQuery(theme.breakpoints.down('xl'));

  return {
    downLg,
    downMd,
    // Down from breakpoint
    downSm,
    downXl,
    isDesktop,
    isLg,
    isMd,
    // Convenience helpers
    isMobile,
    isSm,
    isTablet,
    isXl,
    // Exact breakpoints
    isXs,
    upLg,
    upMd,
    // Up from breakpoint
    upSm,
    upXl,
  };
};

/**
 * Hook to check if screen is at or above a specific breakpoint
 * @param breakpoint - The breakpoint to check ('xs' | 'sm' | 'md' | 'lg' | 'xl')
 */
export const useBreakpointUp = (breakpoint: Breakpoint) => {
  const theme = useMuiTheme();
  return useMediaQuery(theme.breakpoints.up(breakpoint));
};

/**
 * Hook to check if screen is below a specific breakpoint
 * @param breakpoint - The breakpoint to check ('xs' | 'sm' | 'md' | 'lg' | 'xl')
 */
export const useBreakpointDown = (breakpoint: Breakpoint) => {
  const theme = useMuiTheme();
  return useMediaQuery(theme.breakpoints.down(breakpoint));
};

/**
 * Hook to get spacing values from theme
 * @example
 * const spacing = useSpacing();
 * <Box sx={{ padding: spacing(2), margin: spacing(1, 2) }} />
 */
export const useSpacing = () => {
  const theme = useMuiTheme();
  return theme.spacing;
};

/**
 * Hook to access custom colors from theme
 */
export const useColors = () => {
  const theme = useMuiTheme();
  return {
    ...theme.palette,
    custom: theme.customColors,
  };
};
